import { useCallback } from 'react';
import { useNavigate, useRouter, type NavigateOptions } from '@tanstack/react-router';

/** In-app navigation stack (pathnames), maintained by the router subscription in main.tsx. */
export const navStack: string[] = [];

export function trackNavigation(pathname: string, action: 'PUSH' | 'REPLACE' | 'BACK' | 'FORWARD' | 'GO') {
  const top = navStack[navStack.length - 1];
  switch (action) {
    case 'REPLACE':
      if (navStack.length) navStack[navStack.length - 1] = pathname;
      else navStack.push(pathname);
      break;
    case 'BACK':
      if (navStack.length > 1) navStack.pop();
      // if the stack is out of sync, resync to what the browser shows
      if (navStack[navStack.length - 1] !== pathname) navStack.splice(0, navStack.length, pathname);
      break;
    default:
      if (top !== pathname || action !== 'FORWARD') navStack.push(pathname);
  }
  if (navStack.length > 50) navStack.shift();
}

const NOT_A_HOME: RegExp = /^\/(auth|onboarding)(\/|$)/;

/**
 * "Back" that never dumps a logged-in user on the login/onboarding screens:
 * pops browser history when the previous in-app entry is a real screen, otherwise
 * replaces the current entry with `fallback` (default: Today).
 */
export function useBack(fallback: NavigateOptions['to'] = '/') {
  const router = useRouter();
  const navigate = useNavigate();
  return useCallback(() => {
    const prev = navStack[navStack.length - 2];
    if (prev && !NOT_A_HOME.test(prev) && router.history.length > 1) {
      router.history.back(); // the history subscription pops the stack
    } else {
      void navigate({ to: fallback, replace: true });
    }
  }, [router, navigate, fallback]);
}
